import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import { showLoading, closeLoading } from '../Reducer/LoadingReducer';
import Commons from '../Services/Common/Common';
import Global from '../Services/Common/Global';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { openPhotoSourceSheet } from '../Services/PhotoSource/photoSourceSheet';

const CustomImagePicker = registerPlugin('CustomImagePicker');

/**
 * 파일 업로드 관련 공통 Hook
 * @param {Object} options - 옵션 설정
 * @param {string} options.oldFilePathField - 기존 파일 경로 필드명 (예: 'MACHINE_IMAGE_FILE_PATH' 또는 'FILE_PATH')
 * @param {string} options.newFileNameField - 새 파일명 필드명 (예: 'MACHINE_IMAGE_FILE_NAME' 또는 'SAVE_NAME')
 * @param {Function} options.getTargetPath - 최종 저장 경로를 반환하는 함수 (item, custCd) => string
 * @returns {Object} 파일 업로드 관련 함수들
 */
const useFileUpload = (options = {}) => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const [previewList, setPreviewList] = useState([]);
  const {
    oldFilePathField = 'filePath',
    newFileNameField = 'saveName',
    getTargetPath = () => '',
    maxCount = 5,
    multiple = false,
    fileResultType = 'string',
  } = options;

  const normalizeClientPortalPath = (p) => {
    return String(p || '')
      .replace(/\\/g, '/')
      .replace(/^.*(ClientPortal|Cloud_File_Server)\//i, '');
  };
  // 청크 업로드
  const uploadInChunks = async (file, chunkSize = 5 * 1024 * 1024) => {
    const totalChunks = Math.ceil(file.size / chunkSize);
    const filePath = 'PreviewFiles';
    const saveName = file.name;

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('chunk', chunk);
      formData.append('chunkIndex', i);
      formData.append('totalChunks', totalChunks);
      formData.append('fileName', saveName);
      formData.append('UserId', userInfo.userid);
      formData.append('FilePath', filePath);

      await Commons.Axios(
        'chunk',
        Global.file.useUrl + Global.urls.Execute.UploadChunk,
        null,
        formData
      );
    }

    // 병합 요청
    const mergeResponse = await Commons.Axios(
      'post',
      Global.file.useUrl + Global.urls.Execute.MergeChunks,
      null,
      {
        fileName: saveName,
        filePath,
        userId: userInfo.userid,
      }
    );

    return mergeResponse?.data?.filePath || '';
  };

  // 파일 확장자에 따른 PDF 변환 함수
  const ConvertFileToPdf = async (_filePath) => {
    if (!_filePath) {
      console.error('ConvertFileToPdf: filePath가 없습니다.');
      return null;
    }

    try {
      const payload = { FilePath: _filePath };

      // 파일 경로에서 확장자 추출
      const fileName =
        _filePath.split('/').pop() || _filePath.split('\\').pop();
      const ext = fileName.split('.').pop()?.toLowerCase();

      // 확장자에 따라 다른 변환 API 선택
      let convertApiUrl = '';
      switch (ext) {
        case 'hwp':
          convertApiUrl = Global.urls.Execute.ConvertHwpToPdf;
          break;
        case 'doc':
        case 'docx':
        case 'xls':
        case 'xlsx':
        case 'ppt':
        case 'pptx':
          convertApiUrl = Global.urls.Execute.ConvertOfficeToPdf;
          break;
        default:
          console.error(
            `ConvertFileToPdf: 지원하지 않는 파일 형식입니다: ${ext}`
          );
          return null;
      }

      const response = await Commons.Axios(
        'post1',
        Global.file.useUrl + convertApiUrl,
        payload
      );

      // 서버 응답 검증
      if (response?.data?.url) {
        return response.data.url;
      } else {
        console.error('ConvertFileToPdf: 서버 응답이 유효하지 않음', response);
        return null;
      }
    } catch (error) {
      console.error('ConvertFileToPdf: 요청 중 오류 발생', error);
      return null;
    }
  };

  // 미리보기 파일 저장 - GridFileUploadF 공통 함수에서 사용
  const SaveLogicPreview = async (_file) => {
    dispatch(showLoading());

    const MAX_CHUNK_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB
    const filePath = 'PreviewFiles';
    let finalSavePath = '';

    try {
      // ✅ 대용량 파일: 청크 업로드
      if (_file.size > MAX_CHUNK_UPLOAD_SIZE) {
        finalSavePath = await uploadInChunks(_file);
      }
      // ✅ 일반 파일 업로드
      else {
        let formList = new FormData();
        formList.append('UserId', userInfo.userid);
        formList.append('FilePath', filePath);
        formList.append('FormFile', _file);

        const result = await Commons.Axios(
          'file2',
          Global.file.useUrl + Global.urls.Execute.videoUpload,
          null,
          formList
        );

        finalSavePath = result?.data?.filePath || '';
      }

      // ✅ 후처리: 저장 경로 정리 및 파일 변환
      if (finalSavePath) {
        const savePath = normalizeClientPortalPath(finalSavePath);

        const ext = _file.name.split('.').pop()?.toLowerCase();

        // PDF로 변환 가능한 파일 형식: 한글, 워드, 엑셀, 파워포인트
        const convertibleExts = [
          'hwp',
          'doc',
          'docx',
          'xls',
          'xlsx',
          'ppt',
          'pptx',
        ];

        if (convertibleExts.includes(ext)) {
          const pdfUrl = await ConvertFileToPdf(savePath);
          return pdfUrl || null;
        }

        // PDF 파일과 이미지 파일은 그대로 반환
        return savePath;
      } else {
        dispatch(
          showSnackbar({
            message: '파일 업로드 실패 또는 파일 경로 없음',
            type: 'error',
          })
        );
        return null;
      }
    } catch (e) {
      console.error('Error in SaveLogicPreview:', e);
      dispatch(
        showSnackbar({
          message: '파일 업로드 중 오류 발생',
          type: 'error',
        })
      );
      return null;
    } finally {
      dispatch(closeLoading());
    }
  };

  // 파일 저장 (PreviewFiles에서 최종 경로로 이동)
  const SaveLogicFile = async (_item) => {
    const validImages = _item || previewList.filter((img) => !img.deleted);
    if (validImages.length === 0) {
      return fileResultType === 'string' ? '' : [];
    }

    dispatch(showLoading());
    try {
      const results = [];
      for (const item of validImages) {
        const oldFilePath = item[oldFilePathField] || '';
        const newFileName = item[newFileNameField];
        const previewFilePath = item['previewFilePath'];

        if (!previewFilePath || !newFileName) continue;

        const targetPath = getTargetPath(item, userInfo.custCd);
        const payload = {
          oldFilePath,
          sourcePreviewPath: previewFilePath,
          newFileName,
          targetPath,
        };

        const { data } = await Commons.Axios(
          'post1',
          Global.file.useUrl + Global.urls.Execute.moveAndReplaceFile,
          payload
        );

        const filePath = data.filePath
          ? normalizeClientPortalPath(data.filePath)
          : `${targetPath}/${newFileName}`;

        if (fileResultType === 'string') {
          results.push(filePath);
        } else {
          results.push({
            ...item,
            filePath: filePath,
          });
        }
      }

      return fileResultType === 'string' ? results.join(',') : results;
    } catch (e) {
      dispatch(
        showSnackbar({ message: e.message || '파일 저장 실패', type: 'error' })
      );
      return null;
    } finally {
      dispatch(closeLoading());
    }
  };

  // 파일 삭제 (moveAndReplaceFile을 통해 파일 제거)
  const DeleteLogicFile = async (_item) => {
    try {
      // items가 배열이면 각각 처리, 단일 객체/문자열이면 배열로 변환
      const itemsArray = Array.isArray(_item) ? _item : [_item];
      const results = [];

      for (const item of itemsArray) {
        // item이 문자열인 경우 그대로 사용, 객체인 경우 filePath 필드 추출
        const filePath =
          typeof item === 'string'
            ? item
            : item?.filePath || item?.[oldFilePathField];

        if (!filePath) {
          continue;
        }

        const payload = {
          oldFilePath: filePath,
          sourcePreviewPath: null,
          newFileName: null,
          targetPath: null,
        };

        const { data } = await Commons.Axios(
          'post1',
          Global.file.useUrl + Global.urls.Execute.moveAndReplaceFile,
          payload
        );

        if (!data || data.type === 'Error') {
          throw new Error(data?.message || '파일 삭제 실패');
        }

        results.push(data);
      }

      return results.length > 0 ? results : null;
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e.message || '파일 삭제 실패',
          type: 'error',
        })
      );
      console.error('DeleteLogicFile Error:', e);
      return null;
    }
  };

  const createPreviewItem = async (file, options = {}) => {
    const { extra = {} } = options;

    const timestamp = dayjs().format('YYYYMMDDHHmmss');
    const saveName = `${timestamp}_${file.name}`;
    const fileToSave = new File([file], saveName, { type: file.type });

    const previewPath = await SaveLogicPreview(fileToSave);
    if (!previewPath) return null;

    const previewUrl = Global.file.useUrl + 'files/' + previewPath;

    const extraObj =
      typeof extra === 'function'
        ? extra({ file, previewPath, previewUrl, saveName })
        : extra;

    return {
      fileName: file.name,
      fileType: file.type,
      preview: previewUrl,
      previewFilePath: previewPath,
      saveName: saveName,
      image: '',
      ...(extraObj || {}),
    };
  };

  const FileChange = async (event) => {
    const filesArray = multiple
      ? Array.from(event.target.files || [])
      : [event.target.files?.[0]].filter(Boolean);

    if (filesArray.length === 0) return;

    const showGlobalLoading = multiple && filesArray.length > 1;
    if (showGlobalLoading) {
      dispatch(showLoading());
    }

    try {
      if (maxCount !== null) {
        const aliveCount = previewList.filter((img) => !img.deleted).length;

        if (aliveCount + filesArray.length > maxCount) {
          dispatch(
            showSnackbar({
              message: `최대 ${maxCount}개까지 업로드할 수 있습니다. (현재: ${aliveCount}개)`,
              type: 'warning',
            })
          );
          return;
        }
      }

      const newItems = [];
      for (const file of filesArray) {
        try {
          const item = await createPreviewItem(file);
          if (item) newItems.push(item);
        } catch (error) {
          console.error(`파일 업로드 실패: ${file.name}`, error);
          dispatch(
            showSnackbar({
              message: `${file.name} 업로드 실패`,
              type: 'error',
            })
          );
        }
      }

      if (newItems.length > 0) {
        setPreviewList((prev) => [...prev, ...newItems]);

        if (multiple && newItems.length > 1) {
          dispatch(
            showSnackbar({
              message: `${newItems.length}개 파일이 업로드되었습니다.`,
              type: 'success',
            })
          );
        }
      }
    } catch (e) {
      console.error('FileChange Error:', e);
      dispatch(
        showSnackbar({
          message: '파일 업로드 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      event.target.value = '';
      if (showGlobalLoading) {
        dispatch(closeLoading());
      }
    }
  };

  const pathToFile = async (path) => {
    const webPath =
      Capacitor.isNativePlatform() && !path.startsWith('content://')
        ? Capacitor.convertFileSrc(path.replace(/^file:\/\//, ''))
        : path.startsWith('file://') || path.startsWith('content://')
          ? path
          : Capacitor.convertFileSrc(path);
    const response = await fetch(webPath);
    if (!response.ok) throw new Error(`파일 로드 실패: ${path}`);
    const blob = await response.blob();
    const fileName =
      path.substring(path.lastIndexOf('/') + 1) || `image_${Date.now()}.jpg`;
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  };

  const addNativeImages = async (paths) => {
    if (!paths?.length) return;

    dispatch(showLoading());
    const newItems = [];

    try {
      for (const path of paths) {
        try {
          const file = await pathToFile(path);
          const item = await createPreviewItem(file);
          if (item) newItems.push(item);
        } catch (error) {
          console.error(`Native 파일 업로드 실패: ${path}`, error);
          dispatch(
            showSnackbar({
              message: '네이티브 사진 업로드 실패',
              type: 'error',
            })
          );
        }
      }

      if (newItems.length > 0) {
        setPreviewList((prev) => [...prev, ...newItems]);
        if (multiple && newItems.length > 1) {
          dispatch(
            showSnackbar({
              message: `${newItems.length}개 파일이 업로드되었습니다.`,
              type: 'success',
            })
          );
        }
      }
    } finally {
      dispatch(closeLoading());
    }
  };

  const NativeFileChange = async () => {
    try {
      const aliveCount = previewList.filter((img) => !img.deleted).length;
      if (maxCount !== null && aliveCount >= maxCount) {
        dispatch(
          showSnackbar({
            message: `최대 ${maxCount}개까지 업로드할 수 있습니다. (현재: ${aliveCount}개)`,
            type: 'warning',
          })
        );
        return;
      }

      const remainCount = maxCount - aliveCount;

      const source = await openPhotoSourceSheet();
      if (!source) return;

      const pickerOptions = {
        maxCount: remainCount,
        buttonText: '완료',
      };

      const result =
        source === 'camera'
          ? await CustomImagePicker.takePhoto()
          : await CustomImagePicker.pickFromGallery(pickerOptions);

      await addNativeImages(result.images || []);
    } catch (e) {
      console.error('NativeFileChange Error:', e);
    }
  };

  const DeletePreview = async (fileItem) => {
    try {
      await DeleteLogicFile(fileItem.previewFilePath);
      setPreviewList((prev) =>
        prev.map((img) => (img === fileItem ? { ...img, deleted: true } : img))
      );
    } catch (e) {
      dispatch(showSnackbar({ message: e.message, type: 'error' }));
    }
  };

  const clearFiles = () => setPreviewList([]);

  return {
    SaveLogicPreview, // 미리보기 파일 저장
    SaveLogicFile, // 파일 저장
    DeleteLogicFile, // 파일 삭제
    clearFiles,
    createPreviewItem,
    FileChange,
    NativeFileChange,
    DeletePreview,

    previewList,
    setPreviewList,
  };
};

export default useFileUpload;
